import React from "react";
import { Drawer, Button, Divider, Spin } from "antd";
import { SHORTCUTS } from "../../../Constant";
import { drawerTitle } from "../../../components";
import {
  CheckoutSummary,
  ChargesAndDiscounts,
  PaymentSection,
  BalanceStatus,
  EmployeeCustomerSelector,
} from "./CashSale";
import { useCashSaleCheckout } from "./CashSale/useCashSaleCheckout";

interface CashSaleDrawerProps {
  open: boolean;
  onClose: () => void;
  onCheckoutSuccess?: (orderId: string) => void;
}

export const CashSaleDrawer: React.FC<CashSaleDrawerProps> = ({
  open,
  onClose,
  onCheckoutSuccess,
}) => {
  const {
    cartTotal,
    discount,
    setDiscount,
    otherCharges,
    setOtherCharges,
    employeeId,
    setEmployeeId,
    customer,
    setCustomer,
    grandTotal,
    balance,
    dueAmount,
    isPaid,
    isCheckoutLoading,
    checkoutMutation,
    handleCheckout,
    handleReset,
    editingOrderId,
    exchangeDiscount,

    cashAmount,
    setCashAmount,
    cardAmount,
    setCardAmount,
    cardTxNo,
    setCardTxNo,
    cardBankAccountId,
    setCardBankAccountId,
    transferAmount,
    setTransferAmount,
    transferTxNo,
    setTransferTxNo,
    transferBankAccountId,
    setTransferBankAccountId,
    cheques,
    setCheques,
    paymentMode,
    isCreditSale,
    bankAccounts,
    paymentModeOptions,
    cashAmountRef,
    cardAmountRef,
    transferAmountRef,
    chequeAmountRef,
    handleCreditSaleChange,
    handlePaymentModeChange,

    employees,
    customers,
    loadingCustomers,
  } = useCashSaleCheckout({ open, onClose, onCheckoutSuccess });

  return (
     <Drawer
      open={open}
      onClose={() => {
        handleReset();
        onClose();
      }}
      width={520}
      placement="right"
      title={drawerTitle(
        editingOrderId ? "Update Order Checkout" : "Cash Sale Checkout",
        editingOrderId
          ? "Update existing transaction details"
          : "Finalize transaction and collect cash",
      )}
      footer={
        <div className="flex flex-col gap-2">
          <div className="flex gap-2">
            <Button
              size="middle"
              className="flex-1 h-9 text-xs rounded-md border-gray-200 text-gray-500"
              onClick={() => {
                handleReset();
                onClose();
              }}
            >
              Cancel
            </Button>
            <Button
              size="middle"
              type="primary"
              loading={checkoutMutation.isPending}
              onClick={handleCheckout}
              className="flex-1 h-9 text-xs font-semibold rounded-md"
            >
              {editingOrderId ? "Confirm Update" : "Confirm Sale"}
            </Button>
          </div>
          <div className="flex justify-between items-center text-[10px] text-gray-400 px-1 border-t border-gray-150 pt-1.5 mt-0.5">
            <span className="flex items-center gap-1">
              <kbd className="px-1 py-0.5 bg-gray-150 border border-gray-200 rounded text-gray-500 font-mono text-[9px] leading-none">
                {SHORTCUTS.CHECKOUT.CYCLE_PAYMENT.name}
              </kbd>
              Cycle Payment
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1 py-0.5 bg-gray-150 border border-gray-200 rounded text-gray-500 font-mono text-[9px] leading-none">
                {SHORTCUTS.CHECKOUT.TOGGLE_CREDIT.name}
              </kbd>
              Toggle Credit Sale
            </span>
            <span className="flex items-center gap-1">
              {SHORTCUTS.CHECKOUT.CONFIRM_SALE.name.split("+").map((n, i) => (
                <kbd
                  key={i}
                  className="px-1 py-0.5 bg-gray-150 border border-gray-200 rounded text-gray-500 font-mono text-[9px] leading-none"
                >
                  {n}
                </kbd>
              ))}
              Confirm Sale
            </span>
          </div>
        </div>
      }
    >
      {isCheckoutLoading ? (
        <div className="flex flex-col items-center justify-center h-48 gap-2">
          <Spin size="small" />
          <span className="text-xs text-gray-400">
            Loading checkout details...
          </span>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {/* Summary */}
          <CheckoutSummary
            cartTotal={cartTotal}
            otherCharges={otherCharges}
            discount={discount}
            grandTotal={grandTotal}
            exchangeDiscount={exchangeDiscount}
          />

          {/* Charges and Discounts */}
          <ChargesAndDiscounts
            cartTotal={cartTotal}
            otherCharges={otherCharges}
            setOtherCharges={setOtherCharges}
            discount={discount}
            setDiscount={setDiscount}
          />

          <Divider className="my-1.5" />

          {/* Payment Section */}
          <PaymentSection
            cashAmount={cashAmount}
            setCashAmount={setCashAmount}
            cardAmount={cardAmount}
            setCardAmount={setCardAmount}
            cardTxNo={cardTxNo}
            setCardTxNo={setCardTxNo}
            cardBankAccountId={cardBankAccountId}
            setCardBankAccountId={setCardBankAccountId}
            transferAmount={transferAmount}
            setTransferAmount={setTransferAmount}
            transferTxNo={transferTxNo}
            setTransferTxNo={setTransferTxNo}
            transferBankAccountId={transferBankAccountId}
            setTransferBankAccountId={setTransferBankAccountId}
            cheques={cheques}
            setCheques={setCheques}
            paymentMode={paymentMode}
            isCreditSale={isCreditSale}
            bankAccounts={bankAccounts}
            paymentModeOptions={paymentModeOptions}
            cashAmountRef={cashAmountRef}
            cardAmountRef={cardAmountRef}
            transferAmountRef={transferAmountRef}
            chequeAmountRef={chequeAmountRef}
            handleCreditSaleChange={handleCreditSaleChange}
            handlePaymentModeChange={handlePaymentModeChange}
          />

          {/* Balance / Outstanding Credit Card */}
          <BalanceStatus
            isPaid={isPaid}
            dueAmount={dueAmount}
            balance={balance}
            customerName={customer?.name}
          />

          <Divider className="my-0.5" />

          {/* Employee & Customer Selector */}
          <EmployeeCustomerSelector
            employeeId={employeeId}
            setEmployeeId={setEmployeeId}
            customer={customer}
            setCustomer={setCustomer}
            employees={employees}
            customers={customers}
            loadingCustomers={loadingCustomers}
          />
        </div>
      )}
    </Drawer>
  );
};
